import React, {useState} from "react";
import styled from "styled-components";
import {useSelector} from "react-redux";
import {useSubscription} from "../../../../subscription/useSubscription";
import {authorBackgroundColour} from "../../../../../style/styleConsts";
import {ChangeControls} from "./ChangeControls";
import Loader from "../../../../elements/Loader";
import IdeaActionButton from "./IdeaActionButton";
import CommentaryEntry from "../../../../idea/commentary/CommentaryEntry";
import {logAction} from "../../../../../logging/logEvent";
import {checkAuthorHomepageForClose} from "../../checkAuthorHomepageForClose";
import {checkQuickUpdateCompleted} from "../../../../idea/modal/checkQuickUpdateCompleted";

const OuterContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    background-color: ${authorBackgroundColour};
    border: solid 1px #c7c7c7;
    border-top: 0;
    margin-bottom: 12px;
`;

const InnerContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    padding-left: 16px;
    padding-right: 16px;
    padding-top: 27px;
    padding-bottom: 11px; 
`;

const getPercentageChange = (selectedAction) => {
    switch (selectedAction) {
        case "minus_25":
            return -0.25;
        case "minus_50":
            return -0.5;
        case "plus_25":
            return 0.25;
        case "plus_50":
            return 0.5;
        case "plus_100":
            return 1.0;
    }
};

const ErrorContainer = styled.div`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    color: white;
    background-color: #8f1717;
    padding-left: 16px;
    padding-right: 16px;
    padding-top: 4px;
    padding-bottom: 4px; 
`;

const ErrorMessage = ({errors}) => {
    if (!errors || errors.length === 0) return null;

    const errrorMessage = errors.join(",");

    return <ErrorContainer className="t-error-message">
        <div>{errrorMessage}</div>
    </ErrorContainer>
};

const QuickEdit = ({
                       ideaId,
                       ideaActions,
                       selectedAction,
                       onActionSelected,
                       commentLengthLimit
                   }) => {
    useSubscription("IDEA_DETAILS", ideaId ? `/ui/api/ideas/${ideaId}` : null, 60);
    const ideaDetails = useSelector(({ ideaDetails }) => ideaDetails);
    const [commentary, setCommentary] = useState("");

    const portfolios = ideaDetails && ideaDetails.data && ideaDetails.data.portfolios;
    if (!portfolios || ideaDetails.data.ideaId !== ideaId) {
        return <OuterContainer className="t-idea-quick-edit">
            <Loader/>
        </OuterContainer>;
    }

    const openPortfolios = portfolios.filter(p => p.open);
    const onClose = () => {
        if (ideaDetails.closing.inProgress) {
            return;
        }

        logAction({
            type: "IDEA_QUICK_CLOSE_BUTTON_CLICKED",
            portfolios: openPortfolios,
            commentary: commentary
        });
        ideaActions.closeIdea(checkAuthorHomepageForClose, openPortfolios, commentary, "TIM2_HOMEPAGE");
    };

    const percentageChange = getPercentageChange(selectedAction);

    const onUpdate = () => {
        if (ideaDetails.updating.inProgress) return;

        logAction({
            type: "IDEA_QUICK_UPDATE_BUTTON_CLICKED",
            percentageChange,
            portfolios: openPortfolios,
            commentary: commentary
        });

        ideaActions.updateIdea(checkQuickUpdateCompleted, openPortfolios, commentary, percentageChange);
    };

    const disabled = ideaDetails.closing.inProgress || ideaDetails.updating.inProgress;
    const commentaryRequired = selectedAction && selectedAction !== "close";

    return <OuterContainer className="t-idea-quick-edit">
        <ErrorMessage errors={ideaDetails.updating.errors}/>
        <InnerContainer>
            <ChangeControls selectedAction={selectedAction}
                            disabled={disabled}
                            onActionSelected={onActionSelected}/>
            <CommentaryEntry title="Commentary"
                             disabled={disabled}
                             commentary={commentary}
                             commentaryRequired={commentaryRequired}
                             onCommentaryUpdated={setCommentary}
                             textLimit={commentLengthLimit}/>
            <IdeaActionButton onClose={onClose}
                              onUpdate={onUpdate}
                              commentary={commentary}
                              selectedAction={selectedAction}
                              closeInProgress={ideaDetails.closing.inProgress}
                              updateInProgress={ideaDetails.updating.inProgress}
                              openPortfolios={openPortfolios}/>
        </InnerContainer>
    </OuterContainer>;
};

export default QuickEdit;
